import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, CheckCircle2, Building2, Layers, Users, Headphones, Rocket, Landmark, Briefcase, Factory, Globe, Cpu, Truck, FlaskConical, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import EnquiryForm from "@/components/EnquiryForm";
import ExploreMore from "@/components/ExploreMore";
import LazyImage from "@/components/LazyImage";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import useSlugAlias from "@/hooks/useSlugAlias";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default solution data (fallback)
const defaultSolutionsData = {
  "it-technology": {
    title: "IT & Technology Office Solutions",
    subtitle: "Agile Workspaces for Innovation",
    description: "Create dynamic work environments that foster collaboration, creativity, and productivity for your tech teams. Purpose-built for software companies, IT services, and tech startups.",
    icon: "Layers",
    heroImage: "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/bwo73cui_Information%20Technology.png",
    features: [
      "Agile workstation setups with easy reconfiguration",
      "Collaboration areas for team huddles and scrums",
      "Expansion-friendly modular layouts for growing teams",
      "Advanced cable management for multiple devices",
      "Standing desk options for developer comfort",
      "Acoustic pods for focused coding sessions",
    ],
    benefits: [
      "40% faster team collaboration",
      "Scalable as you grow",
      "ISO 9001:2015 certified workstation standards",
      "Quick installation with minimal downtime",
    ],
    products: ["Linear Workstations", "Modular Office Corner Workstation", "Height Adjustable Desks", "Phone Booths", "Meeting Pods", "Conference Tables"],
  },
  "corporate-hq": {
    title: "Corporate HQ Furniture Solutions",
    subtitle: "Premium Executive Environments",
    description: "Establish authority and professionalism with sophisticated furniture solutions for your corporate headquarters. Perfect for MNCs, large enterprises, and prestigious corporate offices.",
    icon: "Building2",
    heroImage: "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/vxbsryas_corporate%20HQ.png",
    features: [
      "Premium executive cabin furniture with custom finishes",
      "Signature boardroom setups for impactful meetings",
      "Reception area statements that impress visitors",
      "VIP lounge configurations for client hospitality",
      "Custom finishes and premium materials",
      "Integrated technology solutions for modern needs",
    ],
    benefits: [
      "Impressive client-facing spaces",
      "Reflects corporate stature",
      "Premium material quality",
      "White-glove installation service",
    ],
    products: ["Executive Desks", "Modular Office Corner Workstation", "Boardroom Tables", "Reception Counters", "Executive Chairs", "Filing Systems"],
  },
  "coworking": {
    title: "Co-working Space Solutions",
    subtitle: "Flexible & Community-Focused",
    description: "Build vibrant co-working communities with furniture that supports diverse work styles and rapid reconfiguration. Ideal for shared workspaces, innovation hubs, and entrepreneurship centers.",
    icon: "Users",
    heroImage: "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/wztk7tvt_Coworking%20Spaces.png",
    features: [
      "High-density hot desking solutions",
      "Flexible seating arrangements for different work styles",
      "Phone booths for private calls and focus time",
      "Discussion pods for collaboration",
      "Durable materials for high-traffic usage",
      "Easy-clean surfaces for quick turnover",
    ],
    benefits: [
      "Maximize seating capacity",
      "Quick layout changes",
      "Built for heavy daily use",
      "Attract diverse members",
    ],
    products: ["Hot Desks", "Modular Office Corner Workstation", "Phone Booths", "Meeting Booths", "Locker Systems", "Breakout Furniture"],
  },
  "bpo": {
    title: "BPO & Call Center Solutions",
    subtitle: "Built for 24/7 Operations",
    description: "Purpose-built furniture for round-the-clock operations focused on operator comfort, acoustic isolation, and durability. Designed for call centers, customer support centers, and BPO facilities.",
    icon: "Headphones",
    heroImage: "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/k5b368qp_BPO%20Call%20Centres.png",
    features: [
      "Acoustic panels for noise management",
      "24/7-rated task chairs for continuous use",
      "Integrated cable management systems",
      "Dense workstation layouts for maximum capacity",
      "Quick-change shift configurations",
      "Easy maintenance surfaces",
    ],
    benefits: [
      "Designed for shift-based operations",
      "Better acoustic environment",
      "Built for continuous use",
      "Lower maintenance costs",
    ],
    products: ["Acoustic Workstations", "Modular Office Corner Workstation", "24/7 Chairs", "Training Room Setup"],
  },
  "startups": {
    title: "Startup Office Solutions",
    subtitle: "Agile Spaces for Fast-Growing Teams",
    description: "Flexible, cost-effective furniture solutions that scale with your startup journey. From seed stage to Series funding - we grow with you.",
    icon: "Rocket",
    heroImage: "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/c4732r46_Startups.png",
    features: [
      "Budget-friendly modular setups",
      "Easy expansion as team grows",
      "Collaborative open-plan configurations",
      "Quick delivery and installation",
      "Reconfigurable layouts for pivoting",
      "Modern aesthetic on startup budgets",
    ],
    benefits: [
      "Cost-effective scaling",
      "Ready within 2 weeks",
      "Flexible rental options",
      "Upgrade as you grow",
    ],
    products: ["Modular Desks", "Modular Office Corner Workstation", "Collaborative Tables", "Standing Desks"],
  },
  "banking-finance": {
    title: "Banking, Finance & Insurance Solutions",
    subtitle: "Secure & Professional Workspaces",
    description: "Specialized furniture for BFSI sector with focus on security, privacy, and professional aesthetics. Perfect for banks, insurance offices, and financial institutions.",
    icon: "Briefcase",
    heroImage: "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/74d80rbv_Banking%20%26%20Finance.png",
    features: [
      "Privacy-focused workstation designs",
      "Secure document storage solutions",
      "Premium customer service counters",
      "Executive cabins for client meetings",
      "Branch layout optimization",
      "ADA compliant configurations",
    ],
    benefits: [
      "Enhanced data privacy",
      "Professional customer areas",
      "Durable for high footfall",
      "Quick branch rollouts",
    ],
    products: ["Teller Counters", "Modular Office Corner Workstation", "Secure Cabinets", "Customer Seating", "Executive Desks", "Safe Storage"],
  },
  "office-partitions": {
    title: "Office Partitions & Cabin Systems",
    subtitle: "Beyond Workstations - Complete Space Division Solutions",
    description: "Transform your office with versatile partition systems for executive cabins, corridors, cubicles, and collaborative zones. As a modular office furniture company, we go beyond workstations to provide complete space division solutions that define, organize, and enhance your entire workspace.",
    icon: "Layers",
    heroImage: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1280&q=70",
    features: [
      "Full-height glass partitions for executive cabins",
      "Double glazed partitions for superior acoustics",
      "Grid framed partitions for modern aesthetics",
      "Modular cubicle systems with acoustic privacy",
      "Corridor partition solutions for space optimization",
      "Flexible panel systems for quick reconfiguration",
    ],
    benefits: [
      "Integrated with furniture layout (not separate vendors)",
      "No On-Site Carpentry (Delivered to Install)",
      "Faster execution vs civil contractors",
      "Cost-efficient modular systems",
      "Scalable for future expansion",
      "Clean, corporate finish (no messy site work)",
    ],
    whyChooseTitle: "Why Choose Ezeefit for Partitions?",
    whyChooseItems: [
      { title: "Integrated Solution", desc: "Partitions integrated with furniture layout — single vendor for complete office" },
      { title: "No Site Carpentry", desc: "Factory-manufactured, delivered ready to install — no messy on-site work" },
      { title: "Faster Execution", desc: "Modular systems install 3x faster than civil partition contractors" },
      { title: "Cost Efficient", desc: "Competitive pricing with premium quality and longer lifespan" },
      { title: "Scalable Design", desc: "Easily reconfigure or expand as your office needs change" },
      { title: "Corporate Finish", desc: "Clean, professional aesthetics that elevate your workspace" },
    ],
    products: ["Modular Office Cabin Single, Double & Grid Framed Partitions", "Modular Office Corner Workstation", "Cubicle Systems", "Privacy Screens", "Modular Wall Partitions"],
  },
  "gcc": {
    title: "Global Capability Centers (GCC)",
    subtitle: "Modern Workspaces for Global Operations",
    description: "Furniture solutions for Global Capability Centers and offshore development centers. Built to meet international quality standards with local execution.",
    icon: "Globe",
    heroImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1280&q=70",
    features: [
      "International quality standards",
      "Scalable to large-scale setups",
      "Collaboration zones for global teams",
      "Video conferencing room setups",
      "Comfort-focused seating",
      "Sustainable material options",
    ],
    benefits: [
      "Global compliance standards",
      "Rapid large-scale deployment",
      "Consistent quality across floors",
      "ESG-compliant options",
    ],
    products: ["Premium Workstations", "AV Conference Rooms", "Collaboration Hubs", "Executive Suites", "Acoustic Pods"],
  },
  "fintech": {
    title: "FinTech Company Solutions",
    subtitle: "Modern Spaces for Financial Innovation",
    description: "Contemporary furniture for FinTech startups and scale-ups. Balance the energy of a startup with the credibility of a financial institution.",
    icon: "Cpu",
    heroImage: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1280&q=70",
    features: [
      "Modern tech-forward aesthetics",
      "Secure workstation configurations",
      "Flexible team collaboration areas",
      "War rooms for product launches",
      "Investor meeting rooms",
      "24/7 support team areas",
    ],
    benefits: [
      "Impress investors & clients",
      "Scale with funding rounds",
      "Compliance-ready layouts",
      "Attract top talent",
    ],
    products: ["Modern Workstations", "Glass Cabins", "Meeting Pods", "Lounge Areas", "Tech Tables"],
  },
  "logistics": {
    title: "Logistics & Warehousing Solutions",
    subtitle: "Efficient Workspaces for Supply Chain",
    description: "Functional furniture for logistics companies, warehousing facilities, and supply chain operations. Designed for operational efficiency and durability.",
    icon: "Truck",
    heroImage: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1280&q=70",
    features: [
      "Warehouse office integration",
      "Durable for industrial environments",
      "Quick assembly and relocation",
      "Control room setups",
      "Driver rest areas",
      "Operations center configurations",
    ],
    benefits: [
      "Built for warehouse conditions",
      "Easy relocation",
      "24/7 operations ready",
      "Cost-effective solutions",
    ],
    products: ["Operations Desks", "Warehouse Office Modules", "Control Stations", "Rest Area Furniture", "Storage Systems"],
  },
  "pharma": {
    title: "Pharma Corporate Office Solutions",
    subtitle: "Clean & Compliant Workspaces",
    description: "Furniture solutions for pharmaceutical corporate offices meeting industry compliance standards. Clean aesthetics with regulatory compliance in mind.",
    icon: "FlaskConical",
    heroImage: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=1280&q=70",
    features: [
      "Clean-room compatible materials",
      "Easy to sanitize surfaces",
      "Compliance-ready documentation",
      "R&D lab office integration",
      "Regulatory meeting rooms",
      "Secure document storage",
    ],
    benefits: [
      "Industry compliance ready",
      "Hygienic material choices",
      "Professional aesthetics",
      "Audit-friendly documentation",
    ],
    products: ["Lab Office Furniture", "Clean Desks", "Secure Filing", "Meeting Tables", "Visitor Seating"],
  },
  "data-centers": {
    title: "Data Center Office Solutions",
    subtitle: "Mission-Critical Support Spaces",
    description: "Specialized furniture for data center facilities, NOC centers, and IT infrastructure companies. Support 24/7 operations with purpose-built workspaces.",
    icon: "Server",
    heroImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1280&q=70",
    features: [
      "NOC room configurations",
      "24/7 operator workstations",
      "Multi-monitor desk setups",
      "Cable management for tech",
      "Climate-controlled compatibility",
      "Secure access furniture",
    ],
    benefits: [
      "Built for 24/7 monitoring",
      "Multi-screen workstation setups",
      "Designed for sustained operator focus",
      "Mission-critical durability",
    ],
    products: ["NOC Desks", "Operator Consoles", "Server Room Furniture", "Control Centers", "24/7 Chairs"],
  },
};

const iconMap = { Layers, Building2, Users, Headphones, Rocket, Landmark, Briefcase, Factory, Globe, Cpu, Truck, FlaskConical, Server };

// Group solutions for display
const mainIndustries = ["it-technology", "corporate-hq", "coworking", "bpo", "startups", "banking-finance", "office-partitions"];
const emergingIndustries = ["gcc", "fintech", "logistics", "pharma", "data-centers"];

export default function SolutionsPage() {
  const { industry: urlSlug } = useParams();
  // Renamed URL (old -> new redirect) renders the original solution's content.
  const isDirect = !urlSlug || !!defaultSolutionsData[urlSlug];
  const { aliasSlug, loading: aliasLoading } = useSlugAlias("/solutions", isDirect ? null : urlSlug);
  const industry = isDirect ? urlSlug : aliasSlug || urlSlug;
  const [solutionsData, setSolutionsData] = useState(defaultSolutionsData);
  const [isLoading, setIsLoading] = useState(true);

  // Canonical: set the right URL whether viewing the listing or a sub-industry
  useCanonical(
    industry
      ? `https://ezeefitofficesystems.in/solutions/${urlSlug}`
      : "https://ezeefitofficesystems.in/solutions"
  );
  useMetaDescription("Industry-specific modular office furniture by Ezeefit — IT, BPO, banking, co-working, startups & corporate HQ. Built in Bhiwandi, delivered pan-India.");

  // Fetch images from homepage industrySolutions to sync with admin dashboard
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/industrySolutions`);
        if (response.data && response.data.items?.length > 0) {
          // Update solutionsData with images from admin dashboard
          const updatedData = { ...defaultSolutionsData };
          response.data.items.forEach(item => {
            const slug = item.slug || item.id;
            if (updatedData[slug] && item.image) {
              updatedData[slug].heroImage = item.image;
            }
          });
          setSolutionsData(updatedData);
        }
      } catch (error) {
        console.log("Using default solution images");
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);
  
  if (!isDirect && aliasLoading) {
    return <div className="min-h-[60vh] pt-20" data-testid="solution-alias-loading" />;
  }

  if (industry && solutionsData[industry]) {
    const solution = solutionsData[industry];
    const Icon = iconMap[solution.icon] || Building2;
    document.title = `${solution.title} - Corporate Office Furniture Solutions Mumbai | Ezeefit`;

    return (
      <div className="pt-20" data-testid={`solution-page-${industry}`}>
        <Helmet>
          <meta name="description" content={`${solution.description.substring(0, 120)}. Modular office furniture solutions for corporates in Mumbai by Ezeefit — office furniture manufacturer Mumbai.`} />
          <link rel="canonical" href={`https://ezeefitofficesystems.in/solutions/${urlSlug}`} />
        </Helmet>
        {/* Hero */}
        <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img src={solution.heroImage}
              alt={`${solution.title} - Office furniture solutions by Ezeefit Mumbai`}
              className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Solutions
              </Link>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#F97316] flex items-center justify-center">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase">
                  {solution.subtitle}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {solution.title}
              </h1>
              <p className="text-xl text-[#94A3B8] max-w-2xl mb-8">
                {solution.description}
              </p>
              <Button
                asChild
                className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              >
                <Link to="/contact">
                  Get a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features & Benefits */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                  Key Features
                </h2>
                <div className="space-y-4">
                  {solution.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[#334155]">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* For Office Partitions: Show "Why Choose Ezeefit" instead of Benefits */}
                {solution.whyChooseTitle && solution.whyChooseItems ? (
                  <>
                    <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                      {solution.whyChooseTitle}
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {solution.whyChooseItems.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="p-6 bg-[#F8FAFC] border border-[#E2E8F0]"
                        >
                          <div className="w-10 h-10 bg-[#F97316] flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-5 h-5 text-white" />
                          </div>
                          <h4 className="font-semibold text-[#0F172A] mb-2">{item.title}</h4>
                          <p className="text-sm text-[#64748B]">{item.desc}</p>
                        </motion.div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                      Benefits
                    </h2>
                    <div className="grid grid-cols-2 gap-6">
                      {solution.benefits.map((benefit, index) => (
                        <div
                          key={index}
                          className="p-6 bg-[#F8FAFC] border border-[#E2E8F0]"
                        >
                          <span className="text-[#0F172A] font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <h3 className="text-xl font-bold text-[#0F172A] mt-12 mb-6">
                  Products for This Solution
                </h3>
                <div className="flex flex-wrap gap-3">
                  {solution.products.map((product) => {
                    const nonClickableIndustries = ["gcc", "fintech", "logistics", "pharma", "data-centers"];
                    const isNonClickable = nonClickableIndustries.includes(industry);

                    if (isNonClickable) {
                      return (
                        <span
                          key={product}
                          className="px-4 py-2 bg-[#0F172A] text-white text-sm font-mono cursor-default"
                        >
                          {product}
                        </span>
                      );
                    }

                    const slugOverrides = {
                      "Single, Double & Grid Framed Partitions": "glass-partitions",
                      "Modular Office Cabin Single, Double & Grid Framed Partitions": "glass-partitions",
                      "Modular Wall Partitions": "modular-walls",
                      "Modular Office Corner Workstation": "corner-workstations",
                    };
                    const productSlug = slugOverrides[product] || product.toLowerCase()
                      .replace(/\//g, '-')
                      .replace(/&/g, 'and')
                      .replace(/\s+/g, '-')
                      .replace(/24\/7/g, '24-7');

                    return (
                      <Link
                        key={product}
                        to={`/products/${productSlug}`}
                        state={{ fromIndustry: industry }}
                        className="px-4 py-2 bg-[#0F172A] text-white text-sm font-mono hover:bg-[#F97316] transition-colors cursor-pointer"
                      >
                        {product}
                      </Link>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-[#F8FAFC]">
          <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Ready to Transform Your {industry === "bpo" ? "Operations Center" : "Workspace"}?
            </h2>
            <p className="text-lg text-[#64748B] mb-8">
              Get a customized proposal for your project requirements. See our{" "}
              <Link to="/manufacturing" className="text-[#F97316] underline hover:text-orange-600 font-medium">manufacturing facility</Link>{" "}
              and{" "}
              <Link to="/case-studies" className="text-[#F97316] underline hover:text-orange-600 font-medium">completed projects</Link>.
            </p>
            <Button
              asChild
              className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
            >
              <Link to="/contact">
                Request Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </section>

        <ExploreMore currentPage="solutions" />
      </div>
    );
  }

  // Solutions listing page
  return (
    <div className="pt-20" data-testid="solutions-page">
      <Helmet>
        <title>Industry Solutions | Modular Office Furniture for 13+ Sectors | Ezeefit Office Systems</title>
        <meta name="description" content="Industry-specific modular office furniture by Ezeefit — IT, BPO, banking, co-working, startups & corporate HQ. Built in Bhiwandi, delivered pan-India." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/solutions" />
        <meta property="og:title" content="Industry Solutions | Modular Office Furniture for 13+ Sectors | Ezeefit Office Systems" />
        <meta property="og:description" content="Industry-specific modular office furniture solutions — IT, BPO, banking, co-working, startups and more. Pan-India delivery." />
        <meta property="og:url" content="https://ezeefitofficesystems.in/solutions" />
      </Helmet>
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Industry Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Solutions Tailored to Your Industry
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
              We understand that every industry has unique workspace requirements. 
              Explore our specialized solutions for 13+ industries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Industries Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#0F172A]">Core Industry Solutions</h2>
            <p className="text-[#64748B] mt-2">Proven solutions for established sectors</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainIndustries.map((key, index) => {
              const solution = solutionsData[key];
              const Icon = iconMap[solution.icon] || Building2;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <Link
                    to={`/solutions/${key}`}
                    className="block group h-full"
                    data-testid={`solution-card-${key}`}
                  >
                    <div className="relative overflow-hidden h-full bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#F97316] transition-colors">
                      <div className="aspect-[4/3]">
                        <LazyImage
                          src={solution.heroImage}
                          alt={`${solution.title} - Modular office furniture by Ezeefit Mumbai`}
                          containerClassName="w-full h-full"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <div className="w-12 h-12 bg-[#0F172A] flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-[#F97316]" />
                        </div>
                        <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors line-clamp-2">
                          {solution.title.replace(" Solutions", "").replace(" Office", "")}
                        </h3>
                        <p className="text-sm text-[#64748B] line-clamp-2">
                          {solution.subtitle}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Emerging Industries */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block bg-[#F97316] text-white text-sm font-mono px-4 py-2 uppercase tracking-wider mb-4">
              High-Growth Sectors
            </span>
            <h2 className="text-3xl font-bold text-white">Emerging Industry Solutions</h2>
            <p className="text-[#94A3B8] mt-2">Specialized solutions for fast-growing industries</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {emergingIndustries.map((key, index) => {
              const solution = solutionsData[key];
              const Icon = iconMap[solution.icon] || Building2;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={`/solutions/${key}`}
                    className="block group h-full"
                    data-testid={`solution-card-${key}`}
                  >
                    <div className="bg-white/5 border border-white/10 hover:border-[#F97316] p-6 h-full transition-colors">
                      <div className="w-14 h-14 bg-[#F97316] flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#F97316] transition-colors">
                        {solution.title.replace(" Solutions", "").replace(" Office", "")}
                      </h3>
                      <p className="text-sm text-[#94A3B8]">
                        {solution.subtitle}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-[#F97316] text-sm font-semibold">
                        Explore
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Don't See Your Industry?
              </h2>
              <p className="text-lg text-[#64748B] mb-8">
                We work with businesses across all sectors. Contact us to discuss 
                your specific requirements and get a customized solution.
              </p>
            </div>
            <div className="bg-white p-8 border border-[#E2E8F0]">
              <h3 className="text-xl font-bold text-[#0F172A] mb-6">Quick Enquiry</h3>
              <EnquiryForm compact />
            </div>
          </div>
        </div>
      </section>

      <ExploreMore currentPage="solutions" />
    </div>
  );
}
